// WAAP → Logs → Log Archiving Configurations. The vendor's "Logs" page manages
// where WAAP logs are archived (OSS bucket + path prefix + scope), on the PRIVATE
// surface (the public Open-API has no log-archive/bucket endpoint).
//
// Config LIST (GET /log-archive/configs → private waap2 /log_archive_configs) +
// the OSS Bucket options that feed the config form's Bucket select. Create/edit
// land here once the vendor write-contract is captured — the portal's own save is
// currently blocked by a broken bucket API on the vendor side.
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';

/** One OSS bucket option for the Log Archiving "Bucket" select. */
export interface WaapOSSBucket {
  name: string;
  creation_date: number;
}

/** One Log Archiving configuration row (the vendor "Logs" table). The live account
 *  has zero configs, so the exact vendor field names aren't confirmed yet; unknown
 *  keys pass through and the table reads them best-effort. */
export interface WaapLogArchiveConfig {
  id?: number | string;
  [key: string]: unknown;
}

/** Read the account's Log Archiving configurations. Returns [] when none. */
export function useWaapLogArchiveConfigs(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.logArchiveConfigs),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapLogArchiveConfig[]> => {
      const res = await serviceApiV3.get<WaapLogArchiveConfig[] | null>(
        waapPath(domainId as string, WAAP_AREAS.logArchiveConfigs),
      );
      return res.data ?? [];
    },
  });
}

/** Read the account's OSS buckets (Bucket dropdown). Returns [] when none. */
export function useWaapLogArchiveBuckets(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.logArchiveBuckets),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapOSSBucket[]> => {
      const res = await serviceApiV3.get<WaapOSSBucket[] | null>(
        waapPath(domainId as string, WAAP_AREAS.logArchiveBuckets),
      );
      return res.data ?? [];
    },
  });
}
